<template>
    <BaseLink
        ref="root"
        v-bind="props.link"
        :class="$style.snippet"
        :data-interface-target="`toc-number-${props.rank}`"
    >
        <span :class="$style.position">{{ props.rank }}</span>
        <CardContainer
            appearance="icon"
            :src="props.game.icon"
            alt=""
            :class="$style.icon_override"
        />
        <span :class="$style.text">
            <Typography
                type="label"
                size="l"
                :responsive="false"
                :class="$style.title_override"
            >
                {{ props.game.title }}
            </Typography>
            <Typography
                v-if="props.game.mainCategory"
                type="caption"
                size="all-caps"
                semibold
                :responsive="false"
                :class="$style.category_override"
            >
                {{ props.game.mainCategory }}
            </Typography>
        </span>
    </BaseLink>
</template>
<script setup lang="ts">
import { useTemplateRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import BaseLink from '@/components/BaseLink.vue'
import CardContainer from '@/components/CardContainer.vue'
import Typography from '@/components/Typography.vue'
import { type CtrContext, useCtrTarget } from '@/composables/use-ctr-context'

import type { CollectionGame } from '../use-collection-page-data'

const props = defineProps<{
    game: CollectionGame
    rank: number
    link: { to?: RouteLocationRaw; externalTo?: string }
    ctrContext?: CtrContext
}>()

const root = useTemplateRef('root')

useCtrTarget(
    () => root.value?.$el,
    () => (props.ctrContext ? { target: props.game.hru, ...props.ctrContext } : undefined),
)
</script>
<style module>
.snippet {
    display: grid;
    grid-template-columns: 28px 64px minmax(0, 1fr);
    grid-template-areas: 'position icon text';
    align-items: center;
    column-gap: 12px;
}

.position {
    grid-area: position;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid var(--white-12);
    border-radius: 32px;
    color: var(--white-80);
    font-size: var(--font-size-label-s);
    font-weight: var(--font-weight-accent);
    line-height: 1;
}

.snippet:hover .position {
    background: #d2d2d2;
    color: var(--black);
}

.icon_override {
    grid-area: icon;
    width: 64px;
    transition:
        transform 0.3s cubic-bezier(0.22, 1, 0.36, 1),
        filter 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.icon_override:hover {
    transform: scale(1.08);
    filter: brightness(1.06);
}

@media (prefers-reduced-motion: reduce) {
    .icon_override {
        transition: none;
    }
}

.text {
    grid-area: text;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.title_override {
    align-self: flex-start;
    max-width: 100%;
    /* room for descenders, which overflow: hidden clips at this line-height */
    margin-block: -0.2em;
    padding-block: 0.2em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1;
}

.title_override:hover {
    color: var(--purple-600);
}

.category_override {
    color: var(--white-60);
}

.snippet:hover .category_override {
    color: var(--white);
}

@media (width >= 640px) {
    .snippet {
        grid-template-columns: 72px minmax(0, 1fr);
        grid-template-areas:
            'icon position'
            'icon text';
        align-content: center;
        column-gap: 10px;
        row-gap: 12px;
    }

    .position {
        align-self: end;
    }

    .icon_override {
        width: 72px;
    }

    .text {
        align-self: start;
    }

    .title_override {
        font-size: var(--font-size-label-m);
        font-weight: var(--font-weight-accent);
    }

    .category_override {
        display: none;
    }
}
</style>
